import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';

const useImportantLinkApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/important-links?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  // Normalize URL: if user types "example.com" without a scheme, default to https://
  const normalizeUrl = (raw) => {
    if (!raw) return raw;
    const trimmed = String(raw).trim();
    if (!trimmed) return trimmed;
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/important-links', { ...formData, url: normalizeUrl(formData.url) });
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/important-links/${id}`, { ...formData, url: normalizeUrl(formData.url) });
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await api.delete(`/v1/important-links/${ids[0]}`);
      } else {
        await api.post('/v1/important-links/batch-delete', ids);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'title', label: t('importantLink.title'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'url', label: t('importantLink.url'), options: { filter: false, sort: false,
      customBodyRender: (value) => value ? <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: '#0291EF' }}>{value.length > 50 ? value.substring(0, 50) + '...' : value}</a> : '-'
    } },
    { name: 'description', label: t('importantLink.description'), options: { filter: false, sort: false } },
    { name: 'assignmentCount', label: t('importantLink.assignmentCount'), options: { filter: false, sort: false } },
  ], [t]);

  const objectSchema = useMemo(() => [
    { name: 'title', label: t('importantLink.title'), type: 'text', grid: 12, required: true },
    {
      name: 'url',
      label: t('importantLink.url'),
      type: 'text',
      grid: 12,
      required: true,
      description: t('importantLink.urlHint'),
    },
    { name: 'description', label: t('importantLink.description'), type: 'textarea', grid: 12 },
  ], [t]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};

export default useImportantLinkApi;
